import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { JWT_DURATION_MS, TOKEN_EXPIRY_KEY } from '@/constants/auth';

export interface User {
  id: string;
  email?: string;
  username?: string;
  walletAddress?: string;
  email_verified?: boolean;
  isFirstLogin?: boolean;
  hasProfile?: boolean;
  hasBlockchainProfile?: boolean;
  needsMainnetMint?: boolean;
  avatarUrl?: string;
  authMethod?: 'email' | 'wallet';
  role?: 'user' | 'admin' | 'moderator';
  requiresTermsAccept?: boolean;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  accessToken: localStorage.getItem('accessToken'),
  isAuthenticated: !!localStorage.getItem('accessToken'),
  isLoading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ user: User; accessToken: string }>) => {
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.isAuthenticated = true;
      state.error = null;
      localStorage.setItem('accessToken', action.payload.accessToken);
      // Set token expiry time
      const expiryTime = Date.now() + JWT_DURATION_MS;
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
    },

    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },

    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },

    updateAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload;
      localStorage.setItem('accessToken', action.payload);
    },

    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.isAuthenticated = false;
      state.error = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
  },
});

export const {
  setCredentials,
  setUser,
  updateUser,
  updateAccessToken,
  logout,
  setLoading,
  setError,
} = authSlice.actions;

export default authSlice.reducer;
